import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchConversationMessages,
  fetchConversations,
  sendConversationMessage,
  subscribeToConversationEvents,
  type ConversationMessage,
  type ConversationSummary,
} from '../api/conversations';
import type { UploadedFileMeta } from '../api/uploads';

export function useConversations() {
  return useQuery<ConversationSummary[]>({
    queryKey: ['conversations'],
    queryFn: () => fetchConversations(),
    staleTime: 1000 * 30,
  });
}

export function useConversationMessages(conversationId?: string) {
  return useQuery<ConversationMessage[]>({
    queryKey: ['conversation-messages', conversationId],
    queryFn: () => fetchConversationMessages(conversationId!),
    enabled: Boolean(conversationId),
    refetchInterval: 15000,
  });
}

export function useSendConversationMessage(conversationId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ body, attachments }: { body: string; attachments?: UploadedFileMeta[] }) => {
      if (!conversationId) throw new Error('Conversation not selected');
      return sendConversationMessage(conversationId, body, attachments);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function useConversationEvents(
  conversationId: string | undefined,
  onEvent: (data: { type: string; [key: string]: unknown }) => void,
) {
  useEffect(() => {
    if (!conversationId) return;
    const unsubscribe = subscribeToConversationEvents(conversationId, onEvent);
    return () => {
      unsubscribe?.();
    };
  }, [conversationId, onEvent]);
}
